import React from 'react';
import {
  FaClock,
  FaDatabase,
  FaCheckCircle,
  FaTimesCircle,
  FaPaperPlane,
  FaBolt,
  FaStream,
} from 'react-icons/fa';

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const formatDuration = (ms) => {
  if (ms == null) return null;
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const ActivityFeed = ({ activities = [], loading = false }) => {
  if (loading) {
    return (
      <div className="bg-surface rounded-lg border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="divide-y divide-gray-50">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="px-4 py-3 flex items-center gap-3 animate-pulse">
              <div className="w-8 h-8 bg-gray-200 rounded-lg" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3.5 w-40 bg-gray-200 rounded" />
                <div className="h-3 w-24 bg-gray-100 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-gray-200">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <FaStream className="text-primary-500 text-xs" />
          Recent Activity
        </h2>
        <span className="text-[11px] text-gray-400">{activities.length} events</span>
      </div>

      {activities.length === 0 ? (
        <div className="px-4 py-10 text-center">
          <FaBolt className="mx-auto text-2xl text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">No recent activity</p>
          <p className="text-xs text-gray-400 mt-1">
            Schedule or query executions will appear here
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-50 max-h-[420px] overflow-y-auto">
          {activities.map((event) => {
            const isSuccess = event.success === true || event.success === 1;
            const isSchedule = event.event_type === 'schedule';
            const duration = formatDuration(event.duration_ms);

            return (
              <div
                key={`${event.event_type}-${event.event_id}`}
                className="px-4 py-3 flex items-start gap-3 hover:bg-gray-50/50 transition-colors"
              >
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isSuccess ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
                  }`}
                >
                  {isSuccess ? (
                    <FaCheckCircle className="text-sm" />
                  ) : (
                    <FaTimesCircle className="text-sm" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {event.entity_name ||
                        `${isSchedule ? 'Schedule' : 'Query'} #${event.entity_id}`}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        isSchedule ? 'bg-violet-50 text-violet-600' : 'bg-blue-50 text-blue-600'
                      }`}
                    >
                      {isSchedule ? (
                        <FaClock className="text-[8px]" />
                      ) : (
                        <FaDatabase className="text-[8px]" />
                      )}
                      {isSchedule ? 'Schedule' : 'Query'}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[11px] text-gray-400">
                      {formatRelativeTime(event.event_time)}
                    </span>
                    {duration && <span className="text-[11px] text-gray-400">{duration}</span>}
                    {isSchedule && event.messages_sent != null && event.messages_sent > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-[11px] text-gray-400">
                        <FaPaperPlane className="text-[8px]" />
                        {event.messages_sent} sent
                      </span>
                    )}
                    {!isSchedule && event.cache_hit && (
                      <span className="text-[10px] px-1 py-0.5 rounded bg-amber-50 text-amber-600 font-medium">
                        cache
                      </span>
                    )}
                  </div>

                  {event.error_message && !isSuccess && (
                    <p
                      className="text-[11px] text-red-500 mt-1 truncate"
                      title={event.error_message}
                    >
                      {event.error_message}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;
